<template>
  <details class="box absence-email">
    <summary>Abwesenheit aus E-Mail übernehmen (EXPERIMENTAL!)</summary>
    <div class="mt-4">
      <b-field label="E-Mail-Text">
        <b-input v-model="text" type="textarea" rows="8" maxlength="50000"
          placeholder="E-Mail mit Krankmeldung und Unterrichtshinweisen hier einfügen"
          :disabled="busy" @input="draft = null" />
      </b-field>
      <b-field label="Datum der E-Mail">
        <b-input v-model="receivedDate" type="date" :disabled="busy" @input="draft = null" />
      </b-field>
      <p class="mb-3">Zur KI-Auswertung werden der E-Mail-Text und das E-Mail-Datum
        an OpenAI übertragen. Bitte nur die relevanten Angaben einfügen.</p>
      <b-button :loading="busy" :disabled="!text.trim() || busy" @click="preview">
        Mit KI auswerten
      </b-button>
      <p v-if="error" class="has-text-danger mt-3" role="alert">{{ error }}</p>
      <p v-if="success" class="has-text-success mt-3" role="status">{{ success }}</p>
      <div v-if="draft" class="mt-4">
        <p>Bitte die KI-Vorschläge vor dem Speichern prüfen. Von/Bis und die Gültigkeit der Hinweise
          gelten jeweils einschließlich beider Tage.</p>
        <ul v-if="draft.warnings.length" class="mt-2">
          <li v-for="warning in draft.warnings" :key="warning">{{ warning }}</li>
        </ul>
        <fieldset :disabled="busy">
          <b-field label="Lehrkraft">
            <b-select v-model="draft.teacherId" expanded placeholder="Lehrkraft auswählen">
              <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                {{ teacher.fullName }}
              </option>
            </b-select>
          </b-field>
          <b-field label="Abwesend von">
            <b-input v-model="draft.startDate" type="date" />
          </b-field>
          <b-field label="Abwesend bis einschließlich">
            <b-input v-model="draft.endDate" type="date" />
          </b-field>
          <b-field label="Grund">
            <b-select v-model="draft.reason" placeholder="Grund auswählen">
              <option>Krank</option><option>Beurlaubt</option><option>Fahrt</option>
            </b-select>
          </b-field>
          <p class="mt-3">Unterrichtshinweise werden bei der Planerzeugung anhand von Lehrkraft,
            Datum, Stunde und Klasse als Bemerkung zugeordnet.</p>
          <div class="table-container">
            <table class="table is-fullwidth">
              <thead><tr>
                <th>Stunde</th><th>Klasse</th><th>Bemerkung</th><th>Gültigkeit</th><th></th>
              </tr></thead>
              <tbody>
                <tr v-for="(lesson, index) in draft.lessons" :key="index">
                  <td><b-select v-model="lesson.hour" :aria-label="`Stunde ${index + 1}`">
                    <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                  </b-select></td>
                  <td><b-input v-model="lesson.class" :aria-label="`Klasse ${index + 1}`" /></td>
                  <td><b-input v-model="lesson.comment" type="textarea" rows="2" maxlength="2000"
                    :aria-label="`Bemerkung ${index + 1}`" /></td>
                  <td>
                    <b-field label="Von"><b-input v-model="lesson.startDate" type="date"
                      :aria-label="`Hinweis ${index + 1} gültig von`" /></b-field>
                    <b-field label="Bis einschließlich">
                      <b-input v-model="lesson.endDate" type="date"
                      :aria-label="`Hinweis ${index + 1} gültig bis`" /></b-field>
                    <b-button size="is-small" @click="useAbsenceRange(lesson)">
                      Gesamte Abwesenheit
                    </b-button>
                  </td>
                  <td><b-button :aria-label="`Hinweis ${index + 1} entfernen`"
                    @click="draft.lessons.splice(index, 1)">Entfernen</b-button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <b-button @click="addLesson">
            Hinweis ergänzen
          </b-button>
          <b-button type="is-primary" class="mt-3" :loading="busy" :disabled="!canSave"
            @click="save">Abwesenheit und Hinweise speichern</b-button>
        </fieldset>
      </div>
    </div>
  </details>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import helpers from '../../mixins/helpers';

export default {
  props: { teachers: { type: Array, required: true } },
  data() {
    return {
      text: '',
      receivedDate: moment().format('YYYY-MM-DD'),
      draft: null,
      busy: false,
      error: '',
      success: '',
      hours: ['HU1', 'HU2', ...Array.from({ length: 12 }, (_, i) => String(i + 1))],
    };
  },
  computed: {
    canSave() {
      return !this.busy && this.draft && this.draft.teacherId
        && this.validRange(this.draft.startDate, this.draft.endDate)
        && this.draft.reason && this.draft.lessons.every((row) => row.hour
          && row.class.trim() && row.comment.trim()
          && this.validRange(row.startDate, row.endDate)
          && row.startDate >= this.draft.startDate && row.endDate <= this.draft.endDate);
    },
  },
  methods: {
    validRange(start, end) {
      return moment(start, 'YYYY-MM-DD', true).isValid()
        && moment(end, 'YYYY-MM-DD', true).isValid() && start <= end;
    },
    addLesson() {
      this.draft.lessons.push({
        hour: '1', class: '', comment: '', startDate: '', endDate: '',
      });
    },
    useAbsenceRange(lesson) {
      Object.assign(lesson, { startDate: this.draft.startDate, endDate: this.draft.endDate });
    },
    async preview() {
      this.busy = true;
      this.error = '';
      this.success = '';
      this.draft = null;
      try {
        const result = await axios.post(`${helpers.apihost()}absence_email`, {
          action: 'preview', text: this.text, receivedDate: this.receivedDate,
        });
        this.draft = result.data;
      } catch (error) {
        this.error = error.response?.data?.message || 'E-Mail konnte nicht ausgewertet werden.';
      } finally {
        this.busy = false;
      }
    },
    async save() {
      this.busy = true;
      this.error = '';
      try {
        await axios.post(`${helpers.apihost()}absence_email`, { action: 'save', draft: this.draft });
        this.success = 'Abwesenheit und Unterrichtshinweise gespeichert. Beim Erzeugen des Plans werden die passenden Bemerkungen übernommen.';
        this.draft = null;
        this.text = '';
        this.$emit('saved');
      } catch (error) {
        this.error = error.response?.data?.message || 'Abwesenheit konnte nicht gespeichert werden.';
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
summary { cursor: pointer; font-weight: 600; }
fieldset { border: 0; padding: 0; min-width: 0; }
td:nth-child(2) { min-width: 6rem; }
td:nth-child(3) { min-width: 16rem; width: 60%; }
</style>
